import { CheckOutlined, CloseOutlined, PlusOutlined } from "@ant-design/icons"
import { App, Button, Flex, Form, Typography } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { rowCommitKeyDown } from "../../../../components/EntityTable/editable-row-cells.ts"
import { MODAL_CONFIRM_CONFIG } from "../../../../components/Forms/FormChangedWatcher/FormChangedConfirm.tsx"
import { FormChangedWatcher } from "../../../../components/Forms/FormChangedWatcher/FormChangedWatcher.tsx"
import { InputNumber } from "../../../../components/Input/InputNumber.tsx"
import { TextInput } from "../../../../components/Input/TextInput.tsx"
import { empty } from "../../../../constants/placeholders.ts"
import { validateFormWithScroll } from "../../../../utils/form/validate-form-with-scroll.tsx"
import { isFormChanged } from "../../../../utils/object/is-form-changed.ts"
import {
  formatMaterialMargin,
  materialUnitMargin
} from "../../helpers/material-margin.ts"
import type { SupplierMaterialData } from "../../api/services.types.ts"
import type { SupplierMaterial } from "../../types/types.ts"

/** What the row's controls hold, before it is turned back into wire strings. */
interface MaterialFormValues {
  name: string
  unit?: string | null
  unit_cost?: number | null
  /** Null until the user enters one; `required` is what refuses the save. */
  unit_sale_price: number | null
}

export interface SupplierMaterialEditRowProps {
  record: SupplierMaterial
  mode: "create" | "edit"
  onCommit: (data: SupplierMaterialData) => Promise<void>
  onCancel: () => void
  /** May arrive via the table's `onRow`; composed with the row's own handler. */
  onKeyDown?: React.KeyboardEventHandler<HTMLTableRowElement>
}

const toFormValues = (record: SupplierMaterial): MaterialFormValues => ({
  name: record.name,
  unit: record.unit,
  unit_cost: record.unit_cost === null ? null : Number(record.unit_cost),
  // The blank row carries `""`, and `Number("")` is 0 — which `required`
  // accepts, so a material saved straight after typing its name would go on the
  // price list at $0.00 with nothing on screen saying a price was missing.
  unit_sale_price:
    record.unit_sale_price === null || record.unit_sale_price === ""
      ? null
      : Number(record.unit_sale_price)
})

const toWireData = (values: MaterialFormValues): SupplierMaterialData => ({
  name: values.name.trim(),
  unit: values.unit?.trim() || null,
  // An empty cost is the pass-through case and has to reach the server as an
  // explicit null, or clearing one on an edit would leave the old figure.
  unit_cost:
    values.unit_cost === null || values.unit_cost === undefined
      ? null
      : values.unit_cost.toFixed(2),
  unit_sale_price: (values.unit_sale_price ?? 0).toFixed(2)
})

export const SupplierMaterialEditRow = ({
  record,
  mode,
  onCommit,
  onCancel,
  ...rest
}: SupplierMaterialEditRowProps) => {
  const { t } = useTranslation()
  const { modal } = App.useApp()
  const [form] = Form.useForm<MaterialFormValues>()
  const [saving, setSaving] = useState(false)

  const isCreate = mode === "create"
  const initialValues = toFormValues(record)

  const cost = Form.useWatch("unit_cost", form)
  const salePrice = Form.useWatch("unit_sale_price", form)
  const margin = materialUnitMargin(cost ?? null, salePrice)

  const handleSave = async () => {
    let values: MaterialFormValues
    try {
      values = await validateFormWithScroll(form)
    } catch {
      // The form has already marked the field and scrolled to it.
      return
    }

    try {
      setSaving(true)
      await onCommit(toWireData(values))
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    if (isFormChanged<MaterialFormValues>(form.getFieldsValue(), initialValues)) {
      modal.confirm({ ...MODAL_CONFIRM_CONFIG.cancelChanges, onOk: onCancel })
      return
    }
    onCancel()
  }

  const onKeyDownRow = rowCommitKeyDown(() => void handleSave(), {
    incoming: rest.onKeyDown,
    busy: saving
  })

  return (
    <Form<MaterialFormValues>
      form={form}
      component={false}
      initialValues={initialValues}
      disabled={saving}
    >
      <FormChangedWatcher<MaterialFormValues> form={form} saving={saving} />

      <tr
        {...rest}
        onKeyDown={onKeyDownRow}
        className={isCreate ? "bg-blue-50" : ""}
      >
        <td className="ant-table-cell">
          <Form.Item<MaterialFormValues>
            name="name"
            noStyle
            rules={[
              {
                required: true,
                whitespace: true,
                message: t("supplierMaterials.form.nameRequired")
              },
              { max: 255, message: t("supplierMaterials.form.nameTooLong") }
            ]}
          >
            <TextInput
              autoFocus={isCreate}
              placeholder={t("supplierMaterials.form.namePlaceholder")}
              aria-label={t("supplierMaterials.columns.name")}
            />
          </Form.Item>
        </td>

        <td className="ant-table-cell">
          <Form.Item<MaterialFormValues>
            name="unit"
            noStyle
            normalize={(value) => value || null}
            rules={[{ max: 32, message: t("supplierMaterials.form.unitTooLong") }]}
          >
            {/* A unit is a lowercase symbol ("sqft", "each"); the app-wide
                first-letter capitalization would render it wrong. */}
            <TextInput
              disableAutoCapitalize
              placeholder={t("supplierMaterials.form.unitPlaceholder")}
              aria-label={t("supplierMaterials.columns.unit")}
            />
          </Form.Item>
        </td>

        <td className="ant-table-cell">
          <Form.Item<MaterialFormValues> name="unit_cost" noStyle>
            <InputNumber
              min={0}
              max={9999999999.99}
              precision={2}
              className="w-full"
              prefix="$"
              placeholder={t("supplierMaterials.form.costPlaceholder")}
              aria-label={t("supplierMaterials.columns.unitCost")}
            />
          </Form.Item>
        </td>

        <td className="ant-table-cell">
          <Form.Item<MaterialFormValues>
            name="unit_sale_price"
            noStyle
            rules={[
              {
                required: true,
                message: t("supplierMaterials.form.salePriceRequired")
              }
            ]}
          >
            <InputNumber
              min={0}
              max={9999999999.99}
              precision={2}
              className="w-full"
              prefix="$"
              placeholder={t("supplierMaterials.form.salePricePlaceholder")}
              aria-label={t("supplierMaterials.columns.unitSalePrice")}
            />
          </Form.Item>
        </td>

        <td className="ant-table-cell text-right">
          {margin === null ? (
            <Typography.Text type="secondary">
              {cost === null || cost === undefined
                ? t("supplierMaterials.passThrough")
                : empty}
            </Typography.Text>
          ) : (
            <Typography.Text>{formatMaterialMargin(margin)}</Typography.Text>
          )}
        </td>

        <td className="ant-table-cell">
          <Flex gap="small" justify="center">
            <Button
              variant="outlined"
              color="danger"
              onClick={handleCancel}
              icon={<CloseOutlined />}
              aria-label={t("supplierMaterials.actions.cancel")}
            />
            <Button
              loading={saving}
              variant="outlined"
              color="green"
              onClick={handleSave}
              icon={isCreate ? <PlusOutlined /> : <CheckOutlined />}
              aria-label={
                isCreate
                  ? t("supplierMaterials.actions.add")
                  : t("supplierMaterials.actions.save")
              }
            />
          </Flex>
        </td>
      </tr>
    </Form>
  )
}
